'use client';

/**
 * MaritimeFooterWaves — onde animate solo quando section visibile.
 *
 * 3 SVG wave drift continuo, IntersectionObserver toggles `animationPlayState`.
 * Section fuori viewport → paused, zero compositing. Colori chiari,
 * opacity contenuta. Hidden mobile + motion-reduce.
 */

import React, { useEffect, useRef, useState } from 'react';

interface Props {
  variant?: 'light' | 'dark';
}

const MaritimeFooterWaves: React.FC<Props> = ({ variant = 'light' }) => {
  const isDark = variant === 'dark';
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.05, rootMargin: '50px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const animState = isVisible ? 'running' : 'paused';

  return (
    <div ref={ref} className="relative h-32 md:h-40 overflow-hidden pointer-events-none" aria-hidden>
      {/* Wave 1 — top, slow */}
      <svg
        style={{
          willChange: isVisible ? 'transform' : 'auto',
          transform: 'translateZ(0)',
          animationPlayState: animState,
        }}
        className={`absolute top-0 left-0 w-[200%] hidden md:block motion-reduce:!hidden animate-[wave-drift_25s_linear_infinite] ${isDark ? 'opacity-[0.10]' : 'opacity-[0.18]'}`}
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        fill="none"
      >
        <path d="M0,60 C200,40 400,80 720,60 C1040,40 1240,80 1440,60 L1440,120 L0,120 Z"
          fill={isDark ? '#7dd3fc' : '#7dd3fc'} />
      </svg>

      {/* Wave 2 — middle, reverse */}
      <svg
        style={{
          willChange: isVisible ? 'transform' : 'auto',
          transform: 'translateZ(0)',
          animationPlayState: animState,
        }}
        className={`absolute top-[35%] left-0 w-[200%] hidden md:block motion-reduce:!hidden animate-[wave-drift_18s_linear_infinite_reverse] ${isDark ? 'opacity-[0.08]' : 'opacity-[0.14]'}`}
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        fill="none"
      >
        <path d="M0,80 C300,60 500,100 720,80 C940,60 1140,100 1440,80 L1440,120 L0,120 Z"
          fill={isDark ? '#38bdf8' : '#38bdf8'} />
      </svg>

      {/* Wave 3 — bottom, fast */}
      <svg
        style={{
          willChange: isVisible ? 'transform' : 'auto',
          transform: 'translateZ(0)',
          animationPlayState: animState,
        }}
        className={`absolute bottom-0 left-0 w-[200%] hidden md:block motion-reduce:!hidden animate-[wave-drift_15s_linear_infinite] ${isDark ? 'opacity-[0.12]' : 'opacity-[0.22]'}`}
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        fill="none"
      >
        <path d="M0,100 C200,80 500,120 720,100 C940,80 1240,120 1440,100 L1440,120 L0,120 Z"
          fill={isDark ? '#0ea5e9' : '#0ea5e9'} />
      </svg>

      {/* Fallback static mobile */}
      <svg
        className={`absolute bottom-0 left-0 w-full md:hidden ${isDark ? 'opacity-[0.10]' : 'opacity-[0.16]'}`}
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        fill="none"
      >
        <path d="M0,80 C200,60 500,100 720,80 C940,60 1240,100 1440,80 L1440,120 L0,120 Z"
          fill="#38bdf8" />
      </svg>
    </div>
  );
};

export default MaritimeFooterWaves;
